// MARK://dhk8/pos/lib/clients/receipts
// Tickets — both CLOSED orders (emitted receipts, with the optional CFDI 4.0 block)
// and OPEN orders rendered as preliminary "PRE-CUENTA" bills (no folio fiscal, no UUID).
//
//   • getReceipt(id?)   — a closed-order receipt (customer ticket + CFDI). Used by /tickets/:id.
//   • getPrecuenta(id)  — an open order shaped like a receipt, labelled PRE-CUENTA · no fiscal.
//   • listTickets()     — the /tickets list: closed receipts + open pre-cuentas, newest first.
//   • resendReceipt(id) — ask the backend to send a copy by email / WhatsApp (no client send).
//
// Backed by dhk8-pos-orders (orders + payments) + dhk8-erp-invoices / dhk8-fiscal-cfdi
// for the CFDI block in real builds; in the no-backend preview (`VITE_API_STUB=1`) it
// derives everything from SEED_RECEIPTS + SEED_ORDERS.
//
// Live path: listOrders/getOrder + listOrderPayments from dhk8-pos-orders, listInvoices
// (orderId filter) from dhk8-erp-invoices for the CFDI section. resendReceipt POSTs to
// dhk8-pos-orders' POST /v1/orders/:id/resend-receipt (TODO(backend): not in the
// published contract yet — falls back to a no-op "queued" result if the route 404s).

import { ApiError, apiFetch, getTenantId, svcUrl } from '../api.js'
import type { OrderDto, OrderItem, RegisteredPayment } from '../../stores/order-store.js'
import {
  PREVIEW_STUB, SEED_RECEIPTS, SEED_ORDERS, SEED_FLOOR_STATE,
} from '../seed-preview.js'
import { getOrder, listOrders, listOrderPayments, type BackendOrderDto, type BackendOrderPaymentDto, type OrderListResponse } from './orders.js'
import { listInvoices, type InvoiceDto } from './invoices.js'

export type SatStatus = 'TIMBRADA' | 'CANCELADA' | 'PENDIENTE'

export interface ReceiptCfdi {
  uuid: string
  serie: string
  folio: string
  /** ISO timestamp of the SAT timbrado. */
  stampedAt: string
  satStatus: SatStatus
  rfcReceptor: string
  razonSocialReceptor: string
  usoCfdi: string
  /** Total facturado en centavos (debe cuadrar con order.totalCents). */
  totalCents: number
  xmlUrl: string
  pdfUrl: string
}

export interface ReceiptDto {
  id: string
  order: OrderDto
  payments: RegisteredPayment[]
  tipCents: number
  /** Efectivo recibido del cliente (para calcular el cambio en tenders de efectivo). */
  cashGivenCents: number | null
  changeCents: number
  waiterName: string | null
  tableCode: string | null
  closedAt: string
  /** Datos de timbrado CFDI 4.0, o null si la orden no se ha facturado. */
  cfdi: ReceiptCfdi | null
  /** `'precuenta'` for an open order rendered as a preliminary bill (no folio fiscal,
   *  no UUID, not a payment receipt); `'receipt'` (the default) for an emitted/closed
   *  ticket. Optional so the seed `SEED_RECEIPTS` (all closed) needn't spell it out. */
  kind?: 'receipt' | 'precuenta'
}

export type TicketStatus = 'PAGADO' | 'PRE-CUENTA' | 'CANCELADO'

/** One row in the /tickets list. */
export interface TicketSummary {
  /** Receipt id (closed) or order id (open) — what /tickets/:id resolves. */
  id: string
  kind: 'receipt' | 'precuenta'
  /** Ticket number / folio shown in the row. */
  folio: string
  /** "Mesa M3" / "Barra" / "Para llevar" — where the ticket comes from. */
  tableLabel: string
  /** ISO timestamp used for the time column + newest-first sort. */
  at: string
  totalCents: number
  status: TicketStatus
  /** Tipo de orden (para columnas "Tipo"/origen del listado enriquecido). */
  type?: BackendOrderDto['type']
  /** Comensales (dine-in). */
  covers?: number
  /** # de artículos (líneas vivas, sin canceladas). */
  itemCount?: number
  /** Propina en centavos. */
  tipCents?: number
}

/** Opciones del listado de tickets — rango de fechas + límite + inclusión de cancelados. */
export interface ListTicketsOptions {
  /** ISO 8601 — solo órdenes con openedAt >= from (backend filtra por openedAt). */
  from?: string
  /** ISO 8601 — solo órdenes con openedAt <= to. */
  to?: string
  /** Máximo de órdenes por estado (default 100; súbelo al filtrar por rango). */
  limit?: number
  /** Incluir tickets CANCELADOS (una fetch extra). Default true. */
  includeCancelled?: boolean
}

// ── helpers ─────────────────────────────────────────────────────────────────────

function tableLabelForSession(tableSessionId: string | null | undefined): string {
  if (!tableSessionId) return 'Barra'
  const session = SEED_FLOOR_STATE.activeSessions.find((s) => s.id === tableSessionId)
  const table = session && SEED_FLOOR_STATE.tables.find((t) => t.id === session.tableId)
  return table ? `Mesa ${table.code}` : 'Mesa'
}

function tableLabelForOrder(o: BackendOrderDto): string {
  if (o.type === 'TAKEOUT' || o.type === 'DELIVERY' || o.type === 'DRIVE_THROUGH') return 'Para llevar'
  return tableLabelForSession(o.tableSessionId)
}

const BACKEND_ITEM_STATUS = (s: BackendOrderDto['items'][number]['status']): OrderItem['status'] =>
  s === 'SENT' || s === 'PREPARING' || s === 'READY' || s === 'DELIVERED' || s === 'CANCELLED' ? s : 'PENDING'

/** Build a `ReceiptDto` (kind = 'precuenta') from an OPEN order — the preliminary bill.
 *  No folio fiscal, no UUID; `cfdi` is always null and `payments` empty. */
function precuentaFromOrder(o: BackendOrderDto): ReceiptDto {
  const live = o.items.filter((i) => i.status !== 'CANCELLED')
  const items: OrderItem[] = live.map((i) => ({
    id: i.id,
    productId: i.productId,
    name: i.name,
    quantity: i.quantity,
    unitPriceCents: i.unitPriceCents,
    totalCents: i.totalCents,
    taxRate: i.taxRate,
    modifiers: i.modifiers.map((m) => ({ modifierId: m.modifierId, name: m.name, priceDeltaCents: m.priceDeltaCents })),
    ...(i.notes ? { notes: i.notes } : {}),
    status: BACKEND_ITEM_STATUS(i.status),
  }))
  const order: OrderDto = {
    id: o.id,
    folio: o.folio,
    type: o.type,
    status: 'OPEN',
    ...(o.tableSessionId ? { tableSessionId: o.tableSessionId } : {}),
    covers: o.covers,
    items,
    subAccounts: [],
    subtotalCents: o.subtotalCents,
    taxCents: o.taxCents,
    discountCents: o.discountCents,
    totalCents: o.totalCents,
    tipCents: o.tipCents,
    paidCents: o.paidCents,
    openedAt: o.openedAt,
    closedAt: null,
  }
  return {
    id: o.id,
    order,
    payments: [],
    tipCents: o.tipCents,
    cashGivenCents: null,
    changeCents: 0,
    waiterName: null,
    tableCode: o.tableSessionId ? tableLabelForSession(o.tableSessionId).replace(/^Mesa\s/, '') : null,
    closedAt: o.openedAt ?? new Date().toISOString(),
    cfdi: null,
    kind: 'precuenta',
  }
}

// ── live-path adapters (BackendOrderDto + payments + CFDI invoice → ReceiptDto) ──

function adaptPayments(rows: BackendOrderPaymentDto[]): { payments: RegisteredPayment[]; cashGivenCents: number | null; changeCents: number } {
  const live = rows.filter((p) => p.status !== 'VOIDED')
  const payments: RegisteredPayment[] = live.map((p) => ({
    id: p.id, method: p.method, amountCents: p.amountCents, tipCents: p.tipCents,
    cardLastFour: p.cardLastFour, referenceCode: p.reference, createdAt: p.capturedAt,
    subAccountId: p.subAccountId,
  }))
  const changeCents = live.reduce((s, p) => s + p.changeCents, 0)
  return { payments, cashGivenCents: null, changeCents }
}

function adaptCfdi(inv: InvoiceDto | undefined, orderTotalCents: number): ReceiptCfdi | null {
  if (!inv || !inv.cfdiUuid) return null
  const satStatus: SatStatus = inv.status === 'CANCELLED' ? 'CANCELADA' : inv.status === 'STAMPED' ? 'TIMBRADA' : 'PENDIENTE'
  return {
    uuid: inv.cfdiUuid, serie: inv.serie, folio: inv.folio,
    stampedAt: inv.cfdiStampedAt ?? inv.issuedAt, satStatus,
    rfcReceptor: inv.customer?.rfc ?? '', razonSocialReceptor: inv.customer?.legalName ?? '',
    usoCfdi: inv.cfdiUsage, totalCents: inv.totalCents || orderTotalCents,
    // Lane #178 svcUrl sweep — antes apiOrigin()/v1/invoices/X/{xml,pdf} → 404.
    // dhk8-erp-invoices expone los blobs en /api/invoices/:id/{xml,pdf}.
    xmlUrl: svcUrl('dhk8-erp-invoices', `/api/invoices/${encodeURIComponent(inv.id)}/xml`),
    pdfUrl: svcUrl('dhk8-erp-invoices', `/api/invoices/${encodeURIComponent(inv.id)}/pdf`),
  }
}

function adaptOrder(o: BackendOrderDto, status: OrderDto['status']): OrderDto {
  const items: OrderItem[] = o.items.filter((i) => i.status !== 'CANCELLED').map((i) => ({
    id: i.id, productId: i.productId, name: i.name, quantity: i.quantity, unitPriceCents: i.unitPriceCents,
    totalCents: i.totalCents, taxRate: i.taxRate,
    modifiers: i.modifiers.map((m) => ({ modifierId: m.modifierId, name: m.name, priceDeltaCents: m.priceDeltaCents })),
    ...(i.notes ? { notes: i.notes } : {}),
    status: BACKEND_ITEM_STATUS(i.status),
  }))
  return {
    id: o.id, folio: o.ticketFolio ?? o.folio, type: o.type, status,
    ...(o.tableSessionId ? { tableSessionId: o.tableSessionId } : {}),
    covers: o.covers, items, subAccounts: [],
    subtotalCents: o.subtotalCents, taxCents: o.taxCents, discountCents: o.discountCents,
    totalCents: o.totalCents, tipCents: o.tipCents, paidCents: o.paidCents,
    openedAt: o.openedAt, closedAt: o.closedAt,
    publicTicketUuid: o.publicTicketUuid ?? null,
    closedByUserId: o.closedByUserId ?? null,
  }
}

/** Closed BackendOrderDto + its payments + (optional) CFDI invoice → ReceiptDto. */
async function receiptFromClosedOrder(o: BackendOrderDto, signal?: AbortSignal): Promise<ReceiptDto> {
  const [payments, invoices] = await Promise.all([
    listOrderPayments(o.id, signal).catch(() => [] as BackendOrderPaymentDto[]),
    listInvoices({}, signal).then((all) => all.filter((inv) => inv.orderId === o.id)).catch(() => [] as InvoiceDto[]),
  ])
  const { payments: regPayments, cashGivenCents, changeCents } = adaptPayments(payments)
  return {
    id: o.id, order: adaptOrder(o, 'CLOSED'), payments: regPayments,
    tipCents: o.tipCents, cashGivenCents, changeCents,
    // "Atendió" en el preview prefiere el cajero que CERRÓ la orden (closedByUserId);
    // si no está (no debería en CLOSED) cae al que la ABRIÓ. La UI resuelve el id a
    // displayName via users-lookup; este string es sólo el id raw como fallback.
    waiterName: o.closedByUserId ?? o.openedByUserId ?? null,
    tableCode: o.tableSessionId ? tableLabelForSession(o.tableSessionId).replace(/^Mesa\s/, '') : null,
    closedAt: o.closedAt ?? o.openedAt ?? new Date().toISOString(),
    cfdi: adaptCfdi(invoices[0], o.totalCents),
    kind: 'receipt',
  }
}

// ── reads ───────────────────────────────────────────────────────────────────────

/** Fetch the receipt for a closed order. When `orderId` is omitted the most recent
 *  closed receipt is returned. Live path: GET /v1/orders/:id (or list status=CLOSED) on
 *  dhk8-pos-orders + its payments + the matching CFDI invoice on dhk8-erp-invoices. */
export async function getReceipt(orderId?: string, signal?: AbortSignal): Promise<ReceiptDto | null> {
  if (PREVIEW_STUB) {
    if (!SEED_RECEIPTS.length) return null
    const found = orderId ? SEED_RECEIPTS.find((r) => r.id === orderId || r.order.id === orderId) : undefined
    const base = found ?? (orderId ? null : SEED_RECEIPTS[0]!)
    return base ? { ...structuredClone(base), kind: 'receipt' as const } : null
  }
  const tenantId = getTenantId()
  if (!tenantId) return null
  try {
    if (orderId) {
      const o = await getOrder(orderId, signal)
      if (o.status !== 'CLOSED' && o.status !== 'CANCELLED') return null
      return receiptFromClosedOrder(o, signal)
    }
    const { items } = await listOrders({ tenantId, status: 'CLOSED', limit: 1, page: 1 }, signal)
    const o = items[0]
    return o ? receiptFromClosedOrder(o, signal) : null
  } catch {
    return null
  }
}

/** Fetch an OPEN order as a preliminary bill (PRE-CUENTA). Live path: GET /v1/orders/:id. */
export async function getPrecuenta(orderId: string, signal?: AbortSignal): Promise<ReceiptDto | null> {
  if (PREVIEW_STUB) {
    const o = SEED_ORDERS.find((x) => x.id === orderId)
    return o ? precuentaFromOrder(structuredClone(o)) : null
  }
  try {
    const o = await getOrder(orderId, signal)
    if (o.status === 'CLOSED' || o.status === 'CANCELLED') return null
    return precuentaFromOrder(o)
  } catch {
    return null
  }
}

/** Resolve a /tickets/:id to its detail — tries closed receipt first, then open
 *  pre-cuenta. With no id, returns the most recent closed receipt (the bare `/receipt`). */
export async function getTicket(id: string | undefined, signal?: AbortSignal): Promise<ReceiptDto | null> {
  if (!id) return getReceipt(undefined, signal)
  return (await getReceipt(id, signal)) ?? (await getPrecuenta(id, signal))
}

// ── ticket-summary mappers (enriquecidos: type/covers/itemCount/tipCents) ─────────

/** Recibo cerrado del seed (ReceiptDto) → fila de ticket. */
function ticketFromSeedReceipt(r: ReceiptDto): TicketSummary {
  return {
    id: r.id,
    kind: 'receipt',
    folio: r.order.folio,
    tableLabel: r.tableCode ? `Mesa ${r.tableCode}` : (r.order.type === 'TAKEOUT' ? 'Para llevar' : 'Barra'),
    at: r.closedAt,
    totalCents: r.order.totalCents,
    status: r.order.status === 'CANCELLED' ? 'CANCELADO' : 'PAGADO',
    type: r.order.type,
    covers: r.order.covers,
    itemCount: r.order.items.length,
    tipCents: r.tipCents,
  }
}

/** Orden ABIERTA (BackendOrderDto) → fila PRE-CUENTA. */
function ticketFromOpenOrder(o: BackendOrderDto): TicketSummary {
  return {
    id: o.id,
    kind: 'precuenta',
    folio: o.folio,
    tableLabel: tableLabelForOrder(o),
    at: o.openedAt ?? new Date().toISOString(),
    totalCents: o.totalCents,
    status: 'PRE-CUENTA',
    type: o.type,
    covers: o.covers,
    itemCount: o.items.filter((i) => i.status !== 'CANCELLED').length,
    tipCents: o.tipCents,
  }
}

/** Orden CERRADA o CANCELADA (BackendOrderDto) → fila de ticket. */
function ticketFromClosedOrder(o: BackendOrderDto): TicketSummary {
  const cancelled = o.status === 'CANCELLED'
  return {
    id: o.id,
    kind: 'receipt',
    folio: o.ticketFolio ?? o.folio,
    tableLabel: tableLabelForOrder(o),
    at: (cancelled ? o.cancelledAt : o.closedAt) ?? o.closedAt ?? o.openedAt ?? new Date().toISOString(),
    totalCents: o.totalCents,
    status: cancelled ? 'CANCELADO' : 'PAGADO',
    type: o.type,
    covers: o.covers,
    itemCount: o.items.filter((i) => i.status !== 'CANCELLED').length,
    tipCents: o.tipCents,
  }
}

/** The /tickets list: closed receipts + open orders (PRE-CUENTA) + cancelled, newest first.
 *  Con `from`/`to` filtra por rango en el servidor (por openedAt) — así se pueden sacar
 *  periodos completos, no solo los últimos N. Live: GET /v1/orders (status=CLOSED|OPEN|CANCELLED)
 *  con from/to/limit en dhk8-pos-orders. */
export async function listTickets(opts: ListTicketsOptions = {}, signal?: AbortSignal): Promise<TicketSummary[]> {
  const { from, to, includeCancelled = true } = opts
  const inRange = (iso: string): boolean => {
    const t = +new Date(iso)
    if (from && t < +new Date(from)) return false
    if (to && t > +new Date(to)) return false
    return true
  }
  if (PREVIEW_STUB) {
    const closed = SEED_RECEIPTS.map(ticketFromSeedReceipt)
      .filter((tk) => includeCancelled || tk.status !== 'CANCELADO')
    const open = SEED_ORDERS
      .filter((o) => o.status !== 'CLOSED' && o.status !== 'CANCELLED')
      .map(ticketFromOpenOrder)
    return [...closed, ...open]
      .filter((tk) => inRange(tk.at))
      .sort((a, b) => +new Date(b.at) - +new Date(a.at))
  }
  const tenantId = getTenantId()
  if (!tenantId) return []
  const limit = opts.limit ?? 100
  const range = { ...(from ? { from } : {}), ...(to ? { to } : {}) }
  try {
    const calls: Array<Promise<OrderListResponse>> = [
      listOrders({ tenantId, status: 'CLOSED', limit, page: 1, ...range }, signal),
      listOrders({ tenantId, status: 'OPEN', limit, page: 1, ...range }, signal),
    ]
    if (includeCancelled) {
      calls.push(listOrders({ tenantId, status: 'CANCELLED', limit, page: 1, ...range }, signal))
    }
    const [closedRes, openRes, cancelledRes] = await Promise.all(calls)
    const closed = closedRes.items.map(ticketFromClosedOrder)
    const open = openRes.items.map(ticketFromOpenOrder)
    const cancelled = cancelledRes ? cancelledRes.items.map(ticketFromClosedOrder) : []
    return [...closed, ...open, ...cancelled].sort((a, b) => +new Date(b.at) - +new Date(a.at))
  } catch {
    return []
  }
}

// ── sends ───────────────────────────────────────────────────────────────────────

export interface ResendChannels {
  /** Destination email address (optional). */
  email?: string
  /** Destination WhatsApp number, E.164 or local (optional). */
  whatsapp?: string
}

export interface ResendResult {
  /** Human-readable destination(s) the copy was sent to, e.g. "ana@correo.com". */
  sentTo: string
}

/** Ask the backend to send a copy of a ticket / pre-cuenta to the customer by email
 *  and/or WhatsApp. The client never composes or sends anything itself. Live path:
 *  POST /v1/orders/:id/resend-receipt { email?, whatsapp? } on dhk8-pos-orders.
 *  TODO(backend): this route is not in the published dhk8-pos-orders contract yet — if
 *  it 404s we degrade to a local "queued" acknowledgement so the UI doesn't hard-fail. */
export async function resendReceipt(receiptId: string, channels: ResendChannels): Promise<ResendResult> {
  const labels: string[] = []
  if (channels.email?.trim()) labels.push(channels.email.trim())
  if (channels.whatsapp?.trim()) labels.push(`WhatsApp ${channels.whatsapp.trim()}`)
  if (labels.length === 0) throw new Error('Indica un correo o un número de WhatsApp')
  if (PREVIEW_STUB) {
    await new Promise((res) => setTimeout(res, 600))
    return { sentTo: labels.join(' y ') }
  }
  const body: Record<string, string> = {}
  if (channels.email?.trim()) body.email = channels.email.trim()
  if (channels.whatsapp?.trim()) body.whatsapp = channels.whatsapp.trim()
  try {
    // Lane #178 svcUrl sweep — antes apiOrigin()/v1/orders/X/resend-receipt → 404.
    // dhk8-pos-orders es el dueño del resend (cuando el endpoint quede expuesto;
    // TODO(backend) sigue pendiente en el contract del servicio).
    const res = await apiFetch<{ ok?: boolean; data?: { sentTo?: string }; sentTo?: string }>(
      svcUrl('dhk8-pos-orders', `/v1/orders/${encodeURIComponent(receiptId)}/resend-receipt`),
      { method: 'POST', body },
    )
    const sentTo = res?.data?.sentTo ?? res?.sentTo
    return { sentTo: sentTo ?? labels.join(' y ') }
  } catch (e) {
    // TODO(backend): drop this fallback once POST /v1/orders/:id/resend-receipt ships.
    if (e instanceof ApiError && e.status === 404) return { sentTo: labels.join(' y ') }
    throw e
  }
}
